import React, { useState, useEffect, useRef } from 'react';
import logoIcon from './logo.svg';
import MainDemo from './MainDemo';
import SecondaryDemo from './SecondaryDemo';
import Contact from './Contact';
import Section3 from './Section3';
import Section4 from './Section4';
import Section5 from './Section5';
import Section6 from './Section6';
import Footer from './Footer';
import './LandingPage.css';

const LandingPage = () => {
  const [isNavbarHovered, setIsNavbarHovered] = useState(false);
  const [isDropdownHovered, setIsDropdownHovered] = useState(false);
  const [shouldShrink, setShouldShrink] = useState(false);

  // Hero Animation States
  const [heroFixedState, setHeroFixedState] = useState('absolute-top');
  const [subtitleFixed, setSubtitleFixed] = useState(false);
  const [textStage, setTextStage] = useState(0);
  const [navPhase, setNavPhase] = useState('phase-start');
  const [mousePos, setMousePos] = useState({ x: -100, y: -100 });
  const [subtitleTyped, setSubtitleTyped] = useState(0);
  const [hasScrolledFromTop, setHasScrolledFromTop] = useState(false);
  const [showScrollHint, setShowScrollHint] = useState(false);

  // Refs
  const heroContainerRef = useRef(null);
  const navbarRef = useRef(null);
  const navbarWrapperRef = useRef(null);
  const navbarContentRef = useRef(null);
  const navbarBrandRef = useRef(null);

  // Keep latest navPhase accessible inside scroll listener
  const navPhaseRef = useRef(navPhase);
  useEffect(() => { navPhaseRef.current = navPhase; }, [navPhase]);

  const setNavPhaseSafe = (next) => {
    setNavPhase((prev) => (prev === next ? prev : next));
  };

  const getNavbarHeight = () => {
    return navbarRef.current?.offsetHeight || 90;
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (!element) return;
    const navbarHeight = getNavbarHeight();
    const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;

    // Pause section auto-scroll engines so nav-driven smooth scroll can finish cleanly.
    window.__disableSectionAutoScrollUntil = Date.now() + 2500;
    window.scrollTo({ top: elementPosition - navbarHeight, behavior: 'smooth' });
  };

  const scrollToTop = () => {
    window.__disableSectionAutoScrollUntil = Date.now() + 2500;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const navProgressRef = useRef(0);

  // During phase-start, move brand center→left using CSS vars
  const setNavbarProgress = (progress01) => {
    const nav = navbarRef.current;
    const wrapper = navbarWrapperRef.current;
    const content = navbarContentRef.current;
    const brand = navbarBrandRef.current;
    if (!nav || !wrapper || !content || !brand) return;

    const p = Math.min(1, Math.max(0, progress01));
    navProgressRef.current = p;

    const wrapperW = wrapper.clientWidth;
    const brandW = brand.offsetWidth;
    const contentStyle = window.getComputedStyle(content);
    const leftPadding = parseFloat(contentStyle.paddingLeft || '0') || 0;

    const centerShiftPx = (wrapperW / 2 - brandW / 2) - leftPadding;

    nav.style.setProperty('--nav-progress', String(p));
    nav.style.setProperty('--nav-center-shift', `${centerShiftPx}px`);
  };

  // --- Mouse tracking for crosshair ---
  useEffect(() => {
    const handleMouseMove = (e) => setMousePos({ x: e.clientX, y: e.clientY });
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // --- Subtitle typing on load ---
  const subtitleText = 'Finally, we have an architecture AI agent';
  useEffect(() => {
    if (subtitleTyped >= subtitleText.length) return;
    const ch = subtitleText[subtitleTyped];
    // Human-like rhythm: pause longer at spaces/punctuation, jitter on letters
    let delay = 29 + Math.random() * 38; // base: 29–67ms
    if (ch === ' ') delay += 25 + Math.random() * 42; // spaces: +25–67ms
    if (ch === ',') delay += 100 + Math.random() * 67; // comma pause
    const timer = setTimeout(() => setSubtitleTyped((prev) => prev + 1), delay);
    return () => clearTimeout(timer);
  }, [subtitleTyped]);

  useEffect(() => {
    const canShowHint =
      subtitleTyped >= subtitleText.length &&
      !subtitleFixed &&
      !hasScrolledFromTop;

    if (!canShowHint) {
      setShowScrollHint(false);
      return;
    }

    const timer = setTimeout(() => setShowScrollHint(true), 2000);
    return () => clearTimeout(timer);
  }, [subtitleTyped, subtitleText.length, subtitleFixed, hasScrolledFromTop]);

  // --- Recalc nav shift on resize/layout change ---
  useEffect(() => {
    const wrapper = navbarWrapperRef.current;
    const content = navbarContentRef.current;
    const brand = navbarBrandRef.current;
    if (!wrapper || !content || !brand) return;

    const recalc = () => {
      if (navPhaseRef.current === 'phase-start') {
        setNavbarProgress(navProgressRef.current);
      }
    };

    // After layout settles (fix initial load)
    requestAnimationFrame(() => requestAnimationFrame(recalc));

    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(recalc);
      ro.observe(wrapper);
      ro.observe(content);
      ro.observe(brand);
      return () => ro.disconnect();
    } else {
      window.addEventListener('resize', recalc);
      return () => window.removeEventListener('resize', recalc);
    }
  }, []);

  // --- Force recalc when re-entering phase-start ---
  useEffect(() => {
    if (navPhase !== 'phase-start') return;
    requestAnimationFrame(() => setNavbarProgress(navProgressRef.current));
  }, [navPhase]);

  // --- Scroll & Navbar Logic ---
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const viewportHeight = window.innerHeight;
      // Only allow collapsing after hero phase ends
      const allowShrink = navPhaseRef.current === 'phase-done';
      if (allowShrink && currentScrollY >= viewportHeight * 0.8) setShouldShrink(true);
      else setShouldShrink(false);
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // --- Hero Scrollytelling Logic ---
  useEffect(() => {
    const handleHeroScroll = () => {
        if (!heroContainerRef.current) return;

        const container = heroContainerRef.current;
        const rect = container.getBoundingClientRect();
        const viewportHeight = window.innerHeight;

        // 1. MANAGE PINNING (Manual Fixed)
        if (rect.top <= 0 && rect.bottom >= viewportHeight) {
            setHeroFixedState('fixed');
        } else if (rect.bottom < viewportHeight) {
            setHeroFixedState('absolute-bottom');
        } else {
            setHeroFixedState('absolute-top');
        }

        // 2. ANIMATION LOGIC
        const scrolledDistance = -rect.top;
        const totalScrollableHeight = container.offsetHeight - viewportHeight;
        setHasScrolledFromTop(scrolledDistance > 2);

        // Threshold: 50vh (half viewport) for the move animation
        const moveThreshold = viewportHeight * 0.5;

        // Detect if hero section has ended
        const heroEnded = rect.bottom < viewportHeight;

        // Grid opacity ref (set on landing-page root)
        const landingPage = container.closest('.landing-page');

        // PHASE 1: Move Subtitle (0 to 50vh)
        if (scrolledDistance >= 0 && scrolledDistance < moveThreshold) {
            const moveProgress = scrolledDistance / moveThreshold;
            container.style.setProperty('--subtitle-progress', moveProgress);
            setSubtitleFixed(false);
            setTextStage(0);

            // Navbar: stay phase-start, slide logo center→left
            setNavPhaseSafe('phase-start');
            setNavbarProgress(moveProgress);

            // Grid: fade in during Phase 1
            if (landingPage) landingPage.style.setProperty('--grid-opacity', moveProgress);

        }

        // PHASE 2: Locked Subtitle & Rotating Text (50vh onwards)
        else if (scrolledDistance >= moveThreshold) {
            container.style.setProperty('--subtitle-progress', 1);
            setSubtitleFixed(true);
            setNavbarProgress(1);

            // Grid: fully visible
            if (landingPage) landingPage.style.setProperty('--grid-opacity', 1);

            // Switch navbar phase based on hero end
            if (heroEnded) {
                setNavPhaseSafe('phase-done');
            } else {
                setNavPhaseSafe('phase-scroll');
            }

            // Calculate text rotation for the remaining height
            const textScrollable = totalScrollableHeight - moveThreshold;
            const textScrolled = scrolledDistance - moveThreshold;
            const textProgress = textScrolled / textScrollable;

            if (textProgress < 0.33) {
                setTextStage(1);
            } else if (textProgress < 0.66) {
                setTextStage(2);
            } else {
                setTextStage(3);
            }
        }

        // Reset if we scroll back up above the section
        else {
            container.style.setProperty('--subtitle-progress', 0);
            setSubtitleFixed(false);
            setTextStage(0);
            setNavbarProgress(0);
            setNavPhaseSafe('phase-start');

            // Grid: hidden
            if (landingPage) landingPage.style.setProperty('--grid-opacity', 0);
        }
    };

    let isUnmounted = false;
    window.addEventListener('scroll', handleHeroScroll, { passive: true });

    // Init after layout settles to avoid early phase jumps.
    const initRaf = requestAnimationFrame(() => requestAnimationFrame(() => {
      if (!isUnmounted) handleHeroScroll();
    }));

    // Fonts can shift layout; run once more after fonts are ready.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        if (!isUnmounted) handleHeroScroll();
      }).catch(() => {});
    }

    return () => {
      isUnmounted = true;
      cancelAnimationFrame(initRaf);
      window.removeEventListener('scroll', handleHeroScroll);
    };
  }, []);

  // Helpers
  const handleLogoClick = () => console.log('Logo clicked');
  const isCollapsed = shouldShrink && !isNavbarHovered;
  const logoSize = 35;
  const wrapperMaxWidth = isCollapsed ? 'var(--navbar-inner-height)' : '1200px';

  return (
    <div id="top" className="landing-page">
      {/* CAD Crosshair Lines - visible after Phase 1 ends */}
      {subtitleFixed && !isDropdownHovered && (
        <>
          <div className="cad-crosshair cad-h" style={{ top: mousePos.y }} />
          <div className="cad-crosshair cad-v" style={{ left: mousePos.x }} />
        </>
      )}

      {/* Navbar */}
      <nav ref={navbarRef} className={`navbar ${navPhase}`} onMouseEnter={() => setIsNavbarHovered(true)} onMouseLeave={() => setIsNavbarHovered(false)}>
        <div ref={navbarWrapperRef} className={`navbar-wrapper ${isCollapsed ? 'collapsed' : ''}`} style={{ maxWidth: wrapperMaxWidth }}>
          <div ref={navbarContentRef} className={`navbar-content ${isCollapsed ? 'collapsed' : ''}`}>
            <div ref={navbarBrandRef} className="navbar-brand" onClick={handleLogoClick}>
              <img src={logoIcon} alt="Logo" className="nav-logo-static" style={{ width: `${logoSize}px`, transform: `rotate(${isCollapsed ? 0 : 90}deg)` }} />
              <span className="brand-text" style={{ maxWidth: isCollapsed ? '0px' : '200px', opacity: isCollapsed ? 0 : 1 }}></span>
            </div>
            <div className="navbar-links" style={{ maxWidth: isCollapsed ? '0px' : '400px', opacity: isCollapsed ? 0 : 1 }}>
              <div
                className="nav-dropdown"
                onMouseEnter={() => setIsDropdownHovered(true)}
                onMouseLeave={() => setIsDropdownHovered(false)}
              >
                <span className="nav-link">Features</span>
                <div className="nav-dropdown-menu">
                  <a href="#section-main-demo" className="dropdown-item" onClick={(e) => {
                    e.preventDefault();
                    scrollToSection('section-main-demo');
                  }}>Vibe the Building</a>
                  <a href="#section-secondary-demo" className="dropdown-item" onClick={(e) => {
                    e.preventDefault();
                    scrollToSection('section-secondary-demo');
                  }}>Chat to Finetune</a>
                  <a href="#section-3" className="dropdown-item" onClick={(e) => {
                    e.preventDefault();
                    scrollToSection('section-3');
                  }}>Digital Assets</a>
                  <a href="#section-4" className="dropdown-item" onClick={(e) => {
                    e.preventDefault();
                    scrollToSection('section-4');
                  }}>View Synchronization</a>
                  <a href="#section-5" className="dropdown-item" onClick={(e) => {
                    e.preventDefault();
                    scrollToSection('section-5');
                  }}>Real-time Statistics</a>
                </div>
              </div>
              <a href="#top" className="nav-link" onClick={(e) => {
                e.preventDefault();
                scrollToTop();
              }}>About</a>
              <a href="#contact" className="nav-link" onClick={(e) => {
                e.preventDefault();
                scrollToSection('contact');
              }}>Contact</a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Scroll Container */}
      <section className="hero-scroll-container" ref={heroContainerRef}>
        <div className={`hero-sticky-content ${heroFixedState}`}>

            {/* 1. Subtitle */}
            <div className="hero-subtitle-cad">
                {subtitleText.slice(0, subtitleTyped)}{!subtitleFixed && <span className="cad-cursor"></span>}
            </div>

            {showScrollHint && (
              <div className="scroll-hint">
                 <div className="scroll-hint-text">( scroll down )</div>
              </div>
            )}

            {/* 2. Rotating Title - Fixed Center Position */}
            <div className={`hero-title-container ${subtitleFixed ? 'visible' : ''}`}>
                <h1 className={`hero-title-line ${textStage === 1 ? 'visible' : ''}`}>
                    <span className="bold-word">generates</span>
                    <span className="rest-text"> <span className="highlight-text">domain-specific </span> parametric 3D.</span>
                </h1>

                <h1 className={`hero-title-line ${textStage === 2 ? 'visible' : ''}`}>
                    <span className="bold-word">propagates</span>
                    <span className="rest-text"> <span className="highlight-text">one</span> tweak to <span className="highlight-text">all</span> views.</span>
                </h1>

                <h1 className={`hero-title-line ${textStage === 3 ? 'visible' : ''}`}>
                    <span className="bold-word">tracks</span>
                    <span className="rest-text"> all statistics <span className="highlight-text">in real-time</span>.</span>
                </h1>
            </div>

        </div>
      </section>

      <div id="section-main-demo" className="page-section"><MainDemo /></div>

      <div id="section-secondary-demo" className="page-section page-section-wide-gap"><SecondaryDemo /></div>

      <div id="section-3" className="page-section page-section-wide-gap"><Section3 /></div>

      <div id="section-4" className="page-section page-section-wide-gap"><Section4 /></div>

      <div id="section-5" className="page-section"><Section5 /></div>

      <div id="section-6" className="page-section"><Section6 /></div>

      <div className="page-section page-section-contact">
        <Contact />
      </div>

      <Footer onAboutClick={scrollToTop} />
    </div>
  );
};

export default LandingPage;
